import React, { useEffect, useState } from 'react'
import Cards from '../../components/cards/Cards'
import Card2 from '../../components/Cards2/Cards2'
import axios from 'axios'
import { Pagination } from '@mui/material'
import { Link, useParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { FiChevronRight, FiPackage } from 'react-icons/fi'
import Filter from '../../components/Filter/Filter'
import CategoryToolbar from '../../components/CategoryToolbar/CategoryToolbar'
import '../../styles/categoryPage.css'

const SKELETON_COUNT = 8
const ITEMS_PER_PAGE = 20

// Métadonnées d'affichage par ressource (catégorie parente) — la seule
// chose qui varie encore par resource ; toute nouvelle sous-catégorie
// d'une resource existante fonctionne automatiquement sans y toucher.
const RESOURCE_META = {
    bureau: { parentLabel: 'Bureau', parentPath: '/Bureau' },
    chaise: { parentLabel: 'Chaise', parentPath: '/Chaise' },
    table: { parentLabel: 'Table', parentPath: '/Table' },
    armoire: { parentLabel: 'Armoire', parentPath: '/Armoire' },
}

/**
 * Page générique pour une sous-catégorie de produit.
 * Remplace les anciens containers statiques (un fichier + un endpoint
 * numéroté par sous-catégorie) par un seul composant paramétré par
 * `resource` (bureau/chaise/table/armoire) et `type` (valeur exacte du
 * champ `type` en base), lus depuis l'URL (`/produits/:resource/:type`),
 * qui interroge l'endpoint générique `GET /:resource/type/:type`.
 */
const SousCategorieContainer = () => {
    const { resource, type } = useParams()
    const { parentLabel, parentPath } = RESOURCE_META[resource] || {}
    const title = type
    const [infob, setInfob] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [current, setCurrent] = useState(1)

    const filtered = infob.filter((p) => {
        const s = search.trim().toLowerCase()
        return !s || p.titre?.toLowerCase().includes(s) || p.reference?.toLowerCase().includes(s)
    })

    const NbPages = Math.ceil(filtered.length / ITEMS_PER_PAGE)
    const startIndex = (current - 1) * ITEMS_PER_PAGE
    const endIndex = startIndex + ITEMS_PER_PAGE
    const dataParPage = filtered.slice(startIndex, endIndex)

    useEffect(() => {
        setIsLoading(true)
        setSearch('')
        axios.get(`${process.env.REACT_APP_API_URL}/${resource}/type/${encodeURIComponent(type)}`)
            .then(res => {
                setInfob(res.data)
                setIsLoading(false)
            })
            .catch(() => setIsLoading(false))
        setCurrent(1)
    }, [resource, type])

    useEffect(() => {
        setCurrent(1)
    }, [search])

    const cards = dataParPage.map(card => (
        resource === 'chaise'
            ? <Card2 nom={card.nom} key={card._id} titre={card.titre} reference={card.reference} description={card.description} prix={card.prix} disponibilité={card.disponibilite} image={card.image} id={card._id} />
            : <Cards nom={card.nom} key={card._id} titre={card.titre} reference={card.reference} description={card.description} prix1={card.prix1} prix2={card.prix2} prix3={card.prix3} disponibilité={card.disponibilite} image={card.image} dimension1={card.dimension1} dimension2={card.dimension2} dimension3={card.dimension3} id={card._id} />
    ))

    const HandelFilter = (filterValue) => {
        const sorted = [...infob]

        if (filterValue === "prixCroissent") {
            sorted.sort((a, b) => a.prix - b.prix)
        } else if (filterValue === "prixDecroissent") {
            sorted.sort((a, b) => b.prix - a.prix)
        } else if (filterValue === "nomA") {
            sorted.sort((a, b) => a.titre.toUpperCase().localeCompare(b.titre.toUpperCase()))
        } else if (filterValue === "nomZ") {
            sorted.sort((a, b) => b.titre.toUpperCase().localeCompare(a.titre.toUpperCase()))
        } else {
            sorted.sort((a, b) => (a.createdAt > b.createdAt ? 1 : a.createdAt < b.createdAt ? -1 : 0))
        }

        setInfob(sorted)
        setCurrent(1)
    }

    const Handelchange = (event, page) => {
        setCurrent(page)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    return (
        <div className='container hd-catpage'>
            <motion.nav
                className="hd-breadcrumb"
                aria-label="breadcrumb"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
            >
                <Link to="/">Accueil</Link>
                <FiChevronRight />
                {parentLabel && parentPath && (
                    <>
                        <Link to={parentPath}>{parentLabel}</Link>
                        <FiChevronRight />
                    </>
                )}
                <span>{title}</span>
            </motion.nav>

            <motion.div
                className="hd-catpage-header"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 }}
            >
                <h1 className="hd-catpage-title">{title}</h1>
                <p className="hd-catpage-count">
                    {isLoading ? 'Chargement…' : `${filtered.length} ${filtered.length > 1 ? 'articles' : 'article'}`}
                </p>
            </motion.div>

            <div className="hd-catpage-toolbar">
                <CategoryToolbar parent={resource} search={search} onSearchChange={setSearch} showCategoryFilter={false} />
                <Filter functionFilter={HandelFilter} />
            </div>

            {isLoading ? (
                <div className="row">
                    {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                        <div className="col-lg-3 col-md-4 col-6 mb-4" key={i}>
                            <div className="hd-skeleton-card">
                                <div className="hd-skeleton-media" />
                                <div className="hd-skeleton-line hd-skeleton-line-lg" />
                                <div className="hd-skeleton-line hd-skeleton-line-sm" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <motion.div className="hd-catpage-empty" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    <FiPackage />
                    <p>Aucun article disponible pour le moment.</p>
                </motion.div>
            ) : (
                <AnimatePresence mode="wait">
                    <motion.div
                        className='row'
                        key={current}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {cards}
                    </motion.div>
                </AnimatePresence>
            )}

            {NbPages > 1 && (
                <div className='container justify-content-center d-flex mt-5 hd-catpage-pagination'>
                    <Pagination color="secondary" count={NbPages} page={current} onChange={Handelchange} />
                </div>
            )}
        </div>
    )
}

export default SousCategorieContainer
